import { categories } from '../../../infrastructure/categories/data/categories';

/**
 * Trouve la catégorie ou sous-catégorie pour un intérêt donné
 * @param {string} interest - L'intérêt à rechercher
 * @param {string} lang - Code de langue ('fr' ou 'en')
 * @returns {Object|null} - Informations trouvées
 */
export const findCategoryInfo = (interest, lang = 'fr') => {
  if (!interest) return null;
  
  const interestLower = interest.toLowerCase();
  
  // Recherche directe par ID de catégorie
  const categoryById = categories.find(cat => cat.id.toLowerCase() === interestLower);
  if (categoryById) {
    return {
      type: 'category',
      id: categoryById.id,
      name: categoryById.labels[lang],
      icon: categoryById.icon
    };
  }
  
  // Recherche par label de catégorie
  for (const category of categories) {
    if (category.labels[lang].toLowerCase() === interestLower) {
      return {
        type: 'category',
        id: category.id,
        name: category.labels[lang],
        icon: category.icon
      };
    }
    
    // Recherche par ID de sous-catégorie
    const subcategoryById = category.subcategories.find(sub => sub.id.toLowerCase() === interestLower);
    if (subcategoryById) {
      return {
        type: 'subcategory',
        categoryId: category.id,
        id: subcategoryById.id,
        name: subcategoryById[lang],
        icon: category.icon
      };
    }
    
    // Recherche par nom de sous-catégorie
    for (const subcategory of category.subcategories) {
      if (subcategory[lang].toLowerCase() === interestLower) {
        return {
          type: 'subcategory',
          categoryId: category.id,
          id: subcategory.id,
          name: subcategory[lang],
          icon: category.icon
        };
      }
    }
  }
  
  // Cas de "autre" si aucune correspondance
  const otherCategory = categories.find(cat => cat.id === 'other');
  return otherCategory ? {
    type: 'category',
    id: 'other',
    name: otherCategory.labels[lang],
    icon: otherCategory.icon
  } : null;
};

/**
 * Obtient l'icône pour un intérêt
 * @param {string} interest - Intérêt
 * @param {Function} t - Fonction de traduction
 * @returns {string} - Émoji à utiliser
 */
export const getInterestIcon = (interest, t) => {
  if (!interest) return '❓';
  
  const lang = t('__.code', { defaultValue: 'fr' }) === 'en' ? 'en' : 'fr';
  const categoryInfo = findCategoryInfo(interest, lang);
  
  // Retourne directement l'émoji de la catégorie
  return categoryInfo ? categoryInfo.icon : '❓';
};

/**
 * Obtient le nom traduit d'un intérêt
 * @param {string} interest - Intérêt à traduire
 * @param {Function} t - Fonction de traduction
 * @returns {string} - Nom traduit
 */
export const getInterestText = (interest, t) => {
  if (!interest) return '';
  
  const lang = t('__.code', { defaultValue: 'fr' }) === 'en' ? 'en' : 'fr';
  const categoryInfo = findCategoryInfo(interest, lang);
  
  return categoryInfo ? categoryInfo.name : interest;
};

/**
 * Normalise les intérêts pour les comparer plus facilement
 * @param {string[]} interests - Liste d'intérêts
 * @param {Function} t - Fonction de traduction
 * @returns {string[]} - Liste d'intérêts normalisés
 */
export const normalizeInterests = (interests, t) => {
  if (!interests || !Array.isArray(interests)) return [];
  return interests.map(interest => getInterestText(interest, t));
};